import React, { useMemo, useState } from "react";

import {
  faListUl,
  faPlus,
  faRefresh,
} from "@fortawesome/pro-regular-svg-icons";

import { AllOperationsModal } from "src/adminPanel/modulesV2/AllOperationsModal";
import {
  OperationActions,
  operationColumns,
  useOperationConfirm,
} from "src/adminPanel/modulesV2/OperationRow";
import { PatchViewerModal } from "src/adminPanel/modulesV2/PatchViewerModal";
import { operationMatchesSearch } from "src/adminPanel/modulesV2/utils";
import { Module, ModuleOperation, useRefreshModule } from "src/api/modulesApi";
import { Button } from "src/base-components/Button";
import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import { TableComp } from "src/base-components/Table";
import { ModuleOperationStatus } from "src/clients/flow-api";
import { useModal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";

type Props = {
  module: Module;
  searchTerm?: string;
  onCreateOperation?: () => void;
  workspaceName?: string;
  orgName?: string;
};

export const ModuleOperationsDetail: React.FC<Props> = ({
  module,
  searchTerm = "",
  onCreateOperation,
  workspaceName,
  orgName,
}) => {
  const refreshModule = useRefreshModule();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const {
    isOpen,
    openModal,
    closeModal,
    afterLeave,
    data: selectedOperation,
  } = useModal<ModuleOperation>();
  const {
    isOpen: isAllOpsOpen,
    openModal: openAllOpsModal,
    closeModal: closeAllOpsModal,
    afterLeave: afterAllOpsLeave,
  } = useModal();
  const {
    confirmAction,
    isConfirmOpen,
    openConfirm,
    handleConfirm,
    clearConfirm,
    closeConfirm,
  } = useOperationConfirm(module.id);

  const handleRefreshModule = async () => {
    setIsRefreshing(true);
    try {
      await refreshModule(module.id);
    } catch {
      toastActions.failure({
        title: "Failed to refresh module",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Outstanding = active + failed (matching backend's get_outstanding_by_module_id)
  const outstandingCount =
    module.activeOperations.length +
    (module.latestResolvedOperation?.status === ModuleOperationStatus.FAILED
      ? 1
      : 0);
  const canCancel = module.spec !== null || outstandingCount > 1;

  const actions: OperationActions = useMemo(
    () => ({
      onView: openModal,
      ...(canCancel
        ? {
            onCancel: (op: ModuleOperation) =>
              openConfirm({ operation: op, type: "cancel" }),
          }
        : {}),
      onRetry: (op: ModuleOperation) =>
        openConfirm({ operation: op, type: "retry" }),
      onMarkCompleted: (op: ModuleOperation) =>
        openConfirm({ operation: op, type: "markCompleted" }),
      onMarkFailed: (op: ModuleOperation) =>
        openConfirm({ operation: op, type: "markFailed" }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canCancel],
  );

  const latestResolvedData = useMemo(
    () =>
      module.latestResolvedOperation ? [module.latestResolvedOperation] : [],
    [module.latestResolvedOperation],
  );

  const confirmLabels = (() => {
    switch (confirmAction?.type) {
      case "cancel":
        return {
          title: "Cancel Operation",
          button: "Cancel Operation",
          verb: "cancel",
        };
      case "retry":
        return {
          title: "Retry Operation",
          button: "Retry Operation",
          verb: "retry",
        };
      case "markCompleted":
        return {
          title: "Mark Operation Completed",
          button: "Mark Completed",
          verb: "mark completed",
        };
      case "markFailed":
        return {
          title: "Mark Operation Failed",
          button: "Mark Failed",
          verb: "mark failed",
        };
      default:
        return undefined;
    }
  })();

  return (
    <div className="space-y-4 p-4">
      {/* Module actions bar */}
      <div className="flex items-center gap-2">
        <Button
          iconLeft={faRefresh}
          loading={isRefreshing}
          size="sm"
          variant="secondary"
          onClick={handleRefreshModule}
        >
          Refresh
        </Button>
        <Button
          iconLeft={faListUl}
          size="sm"
          variant="secondary"
          onClick={openAllOpsModal}
        >
          All Operations
        </Button>
      </div>

      {/* Active Operations */}
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-gray-200 border-b px-4 py-3">
          <h4 className="font-inter-semibold-13px text-gray-900">
            Active Operations ({module.activeOperations.length})
          </h4>
          {onCreateOperation && (
            <Button
              iconLeft={faPlus}
              size="sm"
              variant="secondary"
              onClick={onCreateOperation}
            >
              New Operation
            </Button>
          )}
        </div>
        {module.activeOperations.length === 0 ? (
          <div className="px-4 py-6 text-center font-inter-normal-13px text-gray-500">
            No active operations
          </div>
        ) : (
          <TableComp
            actions={actions}
            columns={operationColumns}
            data={module.activeOperations}
            disableVirtualization
            getRowId={(row) => row.id}
            headerPropsGetter={() => ({
              classNameOverrides: "py-3",
            })}
            rowPropsGetter={(row) => ({
              classNameOverrides:
                searchTerm &&
                operationMatchesSearch(row.original, searchTerm.toLowerCase())
                  ? "bg-indigo-100"
                  : undefined,
            })}
            variant="default"
          />
        )}
      </div>

      {/* Latest Resolved Operation */}
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-gray-200 border-b px-4 py-3">
          <h4 className="font-inter-semibold-13px text-gray-900">
            Latest Resolved Operation
          </h4>
        </div>
        {module.latestResolvedOperation ? (
          <TableComp
            actions={actions}
            columns={operationColumns}
            data={latestResolvedData}
            disableVirtualization
            getRowId={(row) => row.id}
            headerPropsGetter={() => ({
              classNameOverrides: "py-3",
            })}
            rowPropsGetter={(row) => ({
              classNameOverrides:
                searchTerm &&
                operationMatchesSearch(row.original, searchTerm.toLowerCase())
                  ? "bg-indigo-100"
                  : undefined,
            })}
            variant="default"
          />
        ) : (
          <div className="px-4 py-6 text-center font-inter-normal-13px text-gray-500">
            None recorded
          </div>
        )}
      </div>

      <PatchViewerModal
        afterLeave={afterLeave}
        isOpen={isOpen}
        operation={selectedOperation}
        onClose={closeModal}
      />

      <ConfirmationModal
        confirmationButtonText={confirmLabels?.button}
        description={
          confirmAction && confirmLabels ? (
            <span>
              Are you sure you want to {confirmLabels.verb} operation{" "}
              <span className="font-inter-semibold-13px">
                {confirmAction.operation.id.slice(0, 8)}
              </span>{" "}
              for{" "}
              <span className="font-inter-semibold-13px">{module.kind}</span>{" "}
              module
              {workspaceName ? (
                <>
                  {" "}
                  on workspace{" "}
                  <span className="font-inter-semibold-13px">
                    {workspaceName}
                  </span>
                </>
              ) : null}
              {orgName ? (
                <>
                  {" "}
                  (<span className="font-inter-semibold-13px">{orgName}</span>)
                </>
              ) : null}
              ?
            </span>
          ) : undefined
        }
        afterLeave={clearConfirm}
        open={isConfirmOpen}
        title={confirmLabels?.title ?? ""}
        variant={confirmAction?.type === "cancel" ? "danger" : undefined}
        onClose={closeConfirm}
        onConfirm={handleConfirm}
      />

      <AllOperationsModal
        afterLeave={afterAllOpsLeave}
        isOpen={isAllOpsOpen}
        moduleId={module.id}
        moduleKind={module.kind}
        orgName={orgName}
        searchTerm={searchTerm}
        workspaceName={workspaceName}
        onClose={closeAllOpsModal}
      />
    </div>
  );
};
